import {
  BomSource, FindingBom, PreflightCode, PreflightFinding, PreflightLastArrival, PreflightNumbers, PreflightOrder, PreflightResult,
  PreflightSeverity
} from './document-preflight';
import { PreflightDocumentTarget, preflightOrderTarget } from './document-preflight-links';

/** Wording for each finding code. The API returns codes only; the text is the editor's (Ukrainian). */
export const PREFLIGHT_CODE_TEXT: Record<PreflightCode, string> = {
  NOT_COVERED: 'Не вистачає: вільного залишку немає, а товар у дорозі не покриває потребу.',
  COVERED_BY_INCOMING: 'Зараз не вистачає, але потребу покриє товар у дорозі.',
  RESERVATION_EXPIRES_BEFORE_DELIVERY: 'Зараз товар зарезервовано іншими, але ці резерви спливають до дати поставки.',
  FIGURES_DISAGREE: 'Дані розходяться: залишок за таблицею та за списком резервів дають різні висновки. Перевірте вручну.',
  CANNOT_DETERMINE: 'Не вдалося визначити: бракує даних.',
};

/** Wording for the machine flags a finding can carry; an unknown flag is shown as it is. */
export const PREFLIGHT_FLAG_TEXT: Record<string, string> = {
  ON_WAY_NEGATIVE: 'Показник «в дорозі» від\'ємний, враховано як 0.',
  FIGURE_MISSING: 'Бракує одного з показників.',
  SHIPPED_MISSING: 'Невідома відвантажена кількість.',
  LINE_QUANTITY_MISSING: 'У рядку документа не вказано кількість.',
  HOLDER_DEBTS_MISSING: 'Невідома кількість у чужому утриманні.',
  ASSEMBLY_DEMAND_GROSS: 'Фізичного залишку вистачає, але чужі утримання враховано повністю, разом із потребою їхніх збірок: зауваження може бути завищеним.',
  BOM_QUANTITY_MISSING: 'У складі збірки не вказано кількість компонента: склад не перевірено.',
  BOM_CYCLE: 'Збірки в документі посилаються одна на одну: склад не перевірено.',
};

export function preflightSeverityLabel(severity: PreflightSeverity): string {
  return severity >= 100 ? 'Серйозно' : severity > 0 ? 'Увага' : 'Не визначено';
}

export function preflightSeverityTag(severity: PreflightSeverity): 'danger' | 'warning' | 'secondary' {
  return severity >= 100 ? 'danger' : severity > 0 ? 'warning' : 'secondary';
}

/** The product as the user knows it: its code, or its id when the API had no figures for it (no code known). */
export function preflightProductLabel(f: Pick<PreflightFinding, 'prodCode' | 'prodId'>): string {
  return f.prodCode ? f.prodCode : '#' + f.prodId;
}

/** One assembly path of a component's demand, for the modal. */
export function bomSourceText(v: BomSource): string {
  const lines = v.docItemIds.length;
  return `${v.assemblyCode || '#' + v.assemblyProdId}: ${v.perUnit} на одиницю, потрібно ${v.qty} (рядків документа: ${lines})`;
}

/** How a component's need is made up, for the modal; null when the finding has no assembly demand. */
export function bomNeedText(bom: FindingBom | undefined): string | null {
  if (!bom || !(bom.bomNeed > 0)) { return null; }
  return bom.directNeed > 0
    ? `Потреба: власні рядки ${bom.directNeed} + через збірки ${bom.bomNeed}.`
    : `Потреба через збірки: ${bom.bomNeed}.`;
}

/** Short "through assembly X" tail for the confirmation list; '' when there is none. */
export function bomViaShort(bom: FindingBom | undefined): string {
  if (!bom || !bom.via.length) { return ''; }
  const names = [...new Set(bom.via.map(v => v.assemblyCode || '#' + v.assemblyProdId))];
  return (names.length === 1 ? 'Через збірку ' : 'Через збірки ') + names.join(', ') + '.';
}

/** The scope sentence about the bill of materials; null when there is nothing to say. */
export function preflightBomScopeNote(scope: PreflightResult['scope']): string | null {
  if (scope.bomDepth === 'one-level') { return 'Склад збірних виробів перевірено на один рівень.'; }
  if (scope.bomSkipped === 'TOO_MANY_PRODUCTS') { return 'Склад збірних виробів не перевірено: у документі забагато товарів.'; }
  return 'Склад збірних виробів поки не перевіряється.';
}

export interface PreflightNumberColumn {
  key: keyof PreflightNumbers;
  label: string;
  /** Shown in bold: the figure the manager decides on. */
  strong?: boolean;
  /** Shown with a sign, and in the warning colour when negative. */
  signed?: boolean;
  /** Left out when the API did not send the figure (an API older than `ipf-4.1`). */
  optional?: boolean;
}

/**
 * The columns of the numbers table of one finding, in the order the manager wants them (2026-10-23): this invoice's
 * own figures first, then the totals across all documents, then the stock and what is on the way, and last what
 * results for this invoice. "Резерв цього рахунку" is `ownClaim`, the document's own rows in the booking list.
 *
 * "Вільно зі складу зараз" and "Не вистачає зі складу зараз" count the stock only; incoming goods are not in them, and
 * a label that did not say so read as the invoice's deficit (invoice 128988, 2026-10-23). The figure that does include
 * the goods on the way, and the releasing reservations, is `balanceAfterIncoming`: negative is the real deficit. Its
 * header says what it counted in each finding (`preflightBalanceWording`); "Після надходження" below is the base label.
 *
 * "Оплачено, не відвантажено" is `sold` (`PRODSOLD`): the quantity on paid invoices that has not shipped yet, so it still
 * sits in the stock figure. It used to read "Загалом продано", which suggested goods already gone (owner, 2026-10-23).
 */
export const PREFLIGHT_NUMBER_COLUMNS: PreflightNumberColumn[] = [
  { key: 'need', label: 'Потрібно в цьому рахунку' },
  { key: 'ownClaim', label: 'Резерв цього рахунку' },
  { key: 'sold', label: 'Оплачено, не відвантажено' },
  { key: 'reserved', label: 'Загальний резерв' },
  { key: 'stock', label: 'Склад' },
  { key: 'onWay', label: 'В дорозі' },
  { key: 'free', label: 'Вільно зі складу зараз' },
  { key: 'shortfall', label: 'Не вистачає зі складу зараз' },
  { key: 'balanceAfterIncoming', label: 'Після надходження', strong: true, signed: true, optional: true },
];

/**
 * What the signed balance figure (`balanceAfterIncoming`) really includes for one finding, in words. The API adds the goods
 * on the way (`onWay`) and the reservations that are about to lapse (`lapsing`) to the stock balance; when both are 0 it
 * adds nothing, and a header "Після надходження" then promised an arrival that is not happening (invoice 129030, owner
 * 2026-10-24: "-1 after incoming" with nothing in the "В дорозі" column). The wording therefore names what was counted, and
 * says so when nothing was.
 */
export function preflightBalanceWording(n: PreflightNumbers): { column: string; summary: string } {
  const incoming = n.onWay > 0;
  const lapsing = n.lapsing > 0;
  if (incoming && lapsing) { return { column: 'Після надходження і звільнення резервів', summary: 'після надходження і звільнення резервів' }; }
  if (incoming) { return { column: 'Після надходження', summary: 'після надходження' }; }
  if (lapsing) { return { column: 'Після звільнення резервів', summary: 'після звільнення резервів' }; }
  return { column: 'Баланс, надходжень немає', summary: 'надходжень немає, баланс' };
}

/**
 * The columns to show for one finding: all of them, minus an optional one the API did not send, and with the balance
 * column named for what it counted in this finding (`preflightBalanceWording`).
 */
export function preflightColumnsFor(n: PreflightNumbers): PreflightNumberColumn[] {
  return PREFLIGHT_NUMBER_COLUMNS
    .filter(c => !c.optional || Number.isFinite(n[c.key]))
    .map(c => c.key === 'balanceAfterIncoming' ? { ...c, label: preflightBalanceWording(n).column } : c);
}

/** A signed figure: `+1`, `0`, `-6` (a plain hyphen, so it survives a copy into a spreadsheet). */
export function signedNumber(value: number): string {
  return value > 0 ? '+' + value : String(value);
}

/** The text of one cell of the numbers table. */
export function preflightCellText(c: PreflightNumberColumn, n: PreflightNumbers): string {
  const v = n[c.key];
  if (v === undefined || v === null) { return ''; }
  return c.signed ? signedNumber(v) : String(v);
}

/** True when the cell shows a negative signed figure (shown in the warning colour). */
export function preflightCellNegative(c: PreflightNumberColumn, n: PreflightNumbers): boolean {
  const v = n[c.key];
  return !!c.signed && typeof v === 'number' && v < 0;
}

/**
 * The one-line figures in the "На печать" confirmation. With an API that sends `balanceAfterIncoming` it ends in the
 * figure that counts the goods on the way; an older API gets the stock-only shortfall, labelled as such.
 */
export function preflightSummaryNumbers(n: PreflightNumbers): string {
  const base = 'Потрібно ' + n.need + ', вільно зі складу ' + n.free;
  return Number.isFinite(n.balanceAfterIncoming)
    ? base + ', ' + preflightBalanceWording(n).summary + ' ' + signedNumber(n.balanceAfterIncoming as number) + '.'
    : base + ', не вистачає зі складу ' + n.shortfall + '.';
}

/**
 * Purchase-order queue (API `ipf-4.2`, owner 2026-10-25). Logistics does not maintain the order dates, so the table shows the
 * ORDER in which the orders were placed, the vendor's own note, and the quantity still to come; it never shows an expected
 * arrival date and the title says so. Real arrival takes a week or two, or months by sea.
 */
export const PREFLIGHT_QUEUE_TITLE_PLACED = 'Черга замовлень постачальникам (за порядком розміщення, це не дата прибуття)';

/**
 * The title when the API did not send the order's own date (older than `ipf-4.2`): that response is sorted by `DOCVALDATE`, so
 * it must not be called a placement order (PR 352 review).
 */
export const PREFLIGHT_QUEUE_TITLE_PLAIN = 'Відкриті замовлення постачальникам (порядок розміщення недоступний, це не дата прибуття)';

export const PREFLIGHT_QUEUE_COLUMNS = {
  position: '№ в черзі',
  order: 'Замовлення',
  placed: 'Розміщено',
  note: 'Нотатка постачальника про відвантаження',
  open: 'Ще надійде',
};

/** `2026-10-27` -> `27.10.2026`. Plain string work: no `Date`, so no time zone can move the day. Anything else gives ''. */
export function preflightDayText(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso == null ? '' : String(iso));
  return m ? m[3] + '.' + m[2] + '.' + m[1] : '';
}

/**
 * True when the API sent the order's own date (`ipf-4.2`). An older API sends the queue sorted by `DOCVALDATE` and without
 * `orderDate`: the table then shows no date and no position, so it cannot present that order as the order of placing.
 */
export function preflightQueueHasDates(orders: PreflightOrder[]): boolean {
  return orders.length > 0 && orders.every(o => o.orderDate !== undefined);
}

/** One row of the queue table. `position`, `placed` and `note` are empty when the API sent no placement dates. */
export interface PreflightQueueRow {
  position: string | null;
  docNumber: string;
  /** Where the order's link button goes; null = the order cannot be identified, no button. */
  docTarget: PreflightDocumentTarget | null;
  placed: string;
  note: string;
  open: number;
}

/** What the queue table shows for one finding: the title, whether it has dates, and the rows in the order the API sent them. */
export interface PreflightQueueView {
  hasDates: boolean;
  title: string;
  rows: PreflightQueueRow[];
}

/**
 * The queue table of one finding. The rows keep the order of `orders` (the client never sorts). Only a response that carries the
 * placement dates is called a placement order and gets a position, a date and the vendor note; an older response (sorted by
 * `DOCVALDATE`) gets a neutral title and the order number and quantity only.
 */
export function preflightQueueView(orders: PreflightOrder[]): PreflightQueueView {
  const hasDates = preflightQueueHasDates(orders);
  return {
    hasDates,
    title: hasDates ? PREFLIGHT_QUEUE_TITLE_PLACED : PREFLIGHT_QUEUE_TITLE_PLAIN,
    rows: orders.map((o, i) => ({
      position: hasDates ? (i + 1) + ' з ' + orders.length : null,
      docNumber: o.docNumber || '',
      docTarget: preflightOrderTarget(o),
      placed: hasDates ? preflightDayText(o.orderDate) : '',
      note: hasDates ? preflightVendorNoteText(o) : '',
      open: o.open,
    })),
  };
}

/** The vendor's notes of one order as one cell: dates as `dd.MM.yyyy`, words as written, `(+N)` when more were left out, `—` when none. */
export function preflightVendorNoteText(o: Pick<PreflightOrder, 'vendorNotes' | 'vendorNotesMore'>): string {
  const parts = (o.vendorNotes || [])
    .map(n => n.day ? preflightDayText(n.day) : (n.text || ''))
    .filter(t => t !== '');
  if (!parts.length) { return '—'; }
  const more = o.vendorNotesMore && o.vendorNotesMore > 0 ? ' (+' + o.vendorNotesMore + ')' : '';
  return parts.join('; ') + more;
}

/**
 * The last-arrival line of one finding. `undefined` (an older API) gives null: say nothing rather than "none found".
 * Says "against a purchase order" because goods taken in without one are not seen.
 */
export function preflightLastArrivalText(la: PreflightLastArrival | null | undefined): string | null {
  if (la === undefined) { return null; }
  if (la === null) { return 'За замовленнями постачальникам надходжень цього товару не знайдено.'; }
  const qty = Number.isFinite(la.qty) ? ', ' + la.qty + ' шт' : '';
  const order = la.orderDocNumber ? ' (замовлення ' + la.orderDocNumber + ')' : '';
  return 'Останнє надходження за замовленням постачальнику: ' + preflightDayText(la.day) + qty + order + '.';
}
